import { onBeforeUnmount, readonly, ref } from 'vue';
import { addBreadcrumb } from '@/telemetry/sentry';

export interface IdleResetOptions {
  timeoutMs: number;
  promptMs: number;
  isActive: () => boolean;
  onTimeout: () => void | Promise<void>;
}

const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'touchstart',
] as const;

export function useIdleReset(options: IdleResetOptions): {
  pause: () => void;
  resume: () => void;
  continueSession: () => void;
  isPromptVisible: Readonly<ReturnType<typeof ref<boolean>>>;
  secondsRemaining: Readonly<ReturnType<typeof ref<number>>>;
} {
  const promptMs = Math.min(
    Math.max(options.promptMs, 1_000),
    Math.max(options.timeoutMs, 1_000)
  );
  const promptAtMs = Math.max(0, options.timeoutMs - promptMs);
  const isPromptVisible = ref(false);
  const secondsRemaining = ref(Math.ceil(promptMs / 1000));
  let ticker: number | null = null;
  let lastActivityAt = performance.now();
  let paused = false;
  // Re-entrancy guard. onTimeout may navigate / await DB writes /
  // flush Sentry; the 1s tick keeps firing during that window. Without
  // this guard a second tick would trigger a concurrent reset, causing
  // duplicate session-end calls and racing Sentry teardown.
  // See frontend/src/services/IdleWatcher.js:472-481 for the canonical
  // pattern this mirrors.
  let isResetting = false;

  function clear(): void {
    if (ticker !== null) {
      window.clearInterval(ticker);
      ticker = null;
    }
  }

  function resetPrompt(): void {
    isPromptVisible.value = false;
    secondsRemaining.value = Math.ceil(promptMs / 1000);
  }

  function restart(): void {
    lastActivityAt = performance.now();
    resetPrompt();
  }

  async function tick(): Promise<void> {
    if (paused) return;
    if (isResetting) return;

    if (!options.isActive()) {
      restart();
      return;
    }

    const elapsedMs = performance.now() - lastActivityAt;
    if (elapsedMs >= promptAtMs && !isPromptVisible.value) {
      isPromptVisible.value = true;
      addBreadcrumb('entr.idle', 'Idle prompt shown', {
        secondsRemaining: Math.ceil((options.timeoutMs - elapsedMs) / 1000),
      });
    }

    if (isPromptVisible.value) {
      secondsRemaining.value = Math.max(
        0,
        Math.ceil((options.timeoutMs - elapsedMs) / 1000)
      );
    }

    if (elapsedMs >= options.timeoutMs) {
      isResetting = true;
      resetPrompt();
      lastActivityAt = performance.now();
      addBreadcrumb('entr.idle', 'Idle timeout fired', {
        timeoutMs: options.timeoutMs,
      });
      try {
        await options.onTimeout();
      } catch (error) {
        addBreadcrumb('entr.idle', 'onTimeout threw', {
          message: error instanceof Error ? error.message : String(error),
        });
      } finally {
        isResetting = false;
      }
    }
  }

  function schedule(): void {
    clear();
    if (paused) return;
    ticker = window.setInterval(() => {
      void tick();
    }, 1000);
    void tick();
  }

  const onActivity = (): void => {
    if (isPromptVisible.value) return;
    restart();
  };

  for (const event of ACTIVITY_EVENTS) {
    window.addEventListener(event, onActivity, { passive: true });
  }

  schedule();

  onBeforeUnmount(() => {
    clear();
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, onActivity);
    }
  });

  return {
    pause() {
      paused = true;
      clear();
      resetPrompt();
    },
    resume() {
      paused = false;
      restart();
      schedule();
    },
    continueSession() {
      addBreadcrumb('entr.idle', 'Idle prompt dismissed');
      restart();
    },
    isPromptVisible: readonly(isPromptVisible),
    secondsRemaining: readonly(secondsRemaining),
  };
}
